import { useMemo, useState } from 'react';
import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { categoriesKeys } from '@data/constants';
import {
  CategoryApiGetCategoriesRequest,
  CategoryApiGetNestedCategoriesRequest,
  CategoryCreateBody,
  CategoryDetailsView,
  CategoryTreeView,
  CategoryType,
  ListCategorySearchView,
  ListQuestionCategoryView,
  QuestionsApiGetQuestionCategoriesRequest,
} from '@data/sdk/match';
import { MatchingApi } from '@data/utils/axios-instance';
import { APIError } from '@data/utils/types';
import { useDebounce } from '../../../app/utils/hooks/useDebounce.hook';

export const useCategories = (filters: CategoryApiGetCategoriesRequest) =>
  useQuery<ListCategorySearchView, APIError>({
    queryKey: categoriesKeys.list(filters),
    queryFn: async () => {
      const response = await MatchingApi.category.getCategories(filters);
      return response.data;
    },
    // keep the data for as long as the user refreshes the page
    staleTime: Infinity,
    gcTime: Infinity,
  });

export const useCategoriesOnMutate = () =>
  useMutation<ListCategorySearchView, APIError, CategoryApiGetCategoriesRequest, unknown>({
    mutationFn: async (filters) => {
      const response = await MatchingApi.category.getCategories(filters);
      return response.data;
    },
  });

export const useNestedCategories = (
  filters: CategoryApiGetNestedCategoriesRequest,
  enabled?: boolean,
) =>
  useQuery<CategoryTreeView, APIError>({
    queryKey: categoriesKeys.nested(filters),
    queryFn: async () => {
      const response = await MatchingApi.category.getNestedCategories(filters);
      return response.data;
    },
    // keep the data for as long as the user refreshes the page
    staleTime: Infinity,
    gcTime: Infinity,
    enabled,
  });

export const useCategoriesInfiniteQuery = (type: CategoryType) => {
  const [query, setQuery] = useState<CategoryApiGetCategoriesRequest>({
    limit: 15,
    offset: 0,
    searchString: undefined,
    type,
  });

  const debouncedSearch = useDebounce(query.searchString);

  const { data, fetchNextPage, hasNextPage, isLoading, isFetching } = useInfiniteQuery<
    ListCategorySearchView,
    APIError
  >({
    queryKey: categoriesKeys.categories({
      ...query,
      searchString: debouncedSearch,
    }),
    initialPageParam: {
      ...query,
      searchString: debouncedSearch,
    },
    queryFn: async ({ pageParam }) => {
      const response = await MatchingApi.category.getCategories(
        pageParam as CategoryApiGetCategoriesRequest,
      );
      return response.data;
    },
    getNextPageParam: (lastPage, allPages) => {
      const nextOffset = allPages.reduce((prev, next) => prev + next.items.length, 0);

      return nextOffset < lastPage.count
        ? {
            limit: query.limit,
            offset: nextOffset,
            searchString: debouncedSearch || undefined,
            type,
          }
        : undefined;
    },
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
    gcTime: 1000 * 60 * 60,
    staleTime: 1000 * 60 * 60,
  });

  const categories = data?.pages.flatMap((page) => page.items) || [];

  const categoriesOptions = useMemo(
    () =>
      categories.map((category) => ({
        label: category.name,
        value: category.id,
      })),
    [categories],
  );

  const handleSearchChange = (v?: string) => {
    setQuery({ ...query, searchString: v || undefined, offset: 0 });
  };

  return {
    categories,
    categoriesOptions,
    fetchNextPage,
    hasNextPage,
    categoriesLoading: isLoading || isFetching,
    handleSearchChange,
    searchString: query.searchString,
  };
};

export const useQuestionCategories = (params: QuestionsApiGetQuestionCategoriesRequest) =>
  useQuery<ListQuestionCategoryView, APIError>({
    queryKey: categoriesKeys.questionList(params),
    queryFn: async () => {
      const response = await MatchingApi.questions.getQuestionCategories(params);
      return response.data;
    },
  });

const categoriesParams: CategoryApiGetCategoriesRequest = {
  limit: 100,
  offset: 0,
};
export const useCreateCategory = (type: CategoryType) => {
  const queryClient = useQueryClient();

  return useMutation<CategoryDetailsView, APIError, CategoryCreateBody, unknown>({
    mutationFn: async (categoryCreateBody: CategoryCreateBody) => {
      const response = await MatchingApi.category.createCategory({ categoryCreateBody });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: categoriesKeys.list({ ...categoriesParams, type }),
      });
    },
  });
};
